import React, { useState, useEffect, useRef } from 'react';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../config';
import useMessages from '../../hooks/useMessages';
import classes from './MessageModal.module.css';

// Dev-only escape hatch — see useMessages.js. Kept as a separate constant
// here (not imported) since it gates a different piece of logic
// (shownThisSession) than the one in useMessages.js (dismissedMessages).
const devResetMessages = process.env.REACT_APP_RESET_MESSAGES === 'true';

// Messages are plain objects embedded on the festival doc (added via the
// admin panel's SimpleFormIterator, which doesn't assign one) — older/
// unmigrated ones can lack a stable id entirely. imageUrl is already
// guaranteed present (useMessages.js filters out any message without one),
// so it's a safe fallback key — must match useMessages.js's own fallback.
const messageKey = (msg) => msg?.id || msg?.imageUrl;

const MessageModal = ({ festival, group, currentScreen }) => {
  const queue = useMessages(festival, group, currentScreen);
  const [localDismissed, setLocalDismissed] = useState([]);
  const [dismissing, setDismissing] = useState(false);
  const [ready, setReady] = useState(false);
  const shownThisSession = useRef(false);
  const timerRef = useRef(null);

  const filteredQueue = queue.filter((m) => !localDismissed.includes(messageKey(m)));
  const current = filteredQueue[0] || null;

  useEffect(() => {
    if (!devResetMessages && shownThisSession.current) return;
    clearTimeout(timerRef.current);

    if (!current) return;

    if (currentScreen === 'home') {
      timerRef.current = setTimeout(() => {
        shownThisSession.current = true;
        setReady(true);
      }, 8000);
    } else {
      shownThisSession.current = true;
      setReady(true);
    }

    return () => clearTimeout(timerRef.current);
  }, [currentScreen, current && messageKey(current)]);

  if (!current || !ready) return null;

  const handleDismiss = () => {
    if (dismissing) return;
    setDismissing(true);
    // arrayUnion() throws synchronously (before updateDoc's promise even
    // exists, so .catch() below can't help) if handed undefined, which used
    // to crash the whole modal for a message with no id.
    const key = messageKey(current);
    setTimeout(() => {
      setLocalDismissed((prev) => [...prev, key]);
      setDismissing(false);
      updateDoc(doc(db, 'groups', group.id), {
        dismissedMessages: arrayUnion(key),
      }).catch((err) => console.error('Failed to dismiss message:', err));
    }, 200);
  };

  return (
    <div className={`${classes.backdrop} ${dismissing ? classes.backdropOut : ''}`}>
      <div className={`${classes.modal} ${dismissing ? classes.modalOut : ''}`}>
        <button className={classes.dismissBtn} onClick={handleDismiss} aria-label="Dismiss">
          ×
        </button>
        {devResetMessages && (
          <span className={classes.devBadge}>DEV: messages reset</span>
        )}
        <img src={current.imageUrl} alt="" className={classes.image} />
      </div>
    </div>
  );
};

export default MessageModal;
